import { useState, useEffect, ChangeEvent } from 'react';
import {
    Carrier,
    CoverageType,
    InsuranceType,
    PickPolicyCreateRequestExcludeKeyofPolicyCreateRequestCarrierId,
    Policy,
    PolicyUpdateRequest,
} from '@bluefalcon/clients-management-client-fetch';
import { carriersService } from '../../services';
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    IconButton,
    Box,
    Spinner,
    Flex,
    Button,
    useDisclosure,
    Input,
} from '@chakra-ui/react';
import { AddEditPolicyModal } from '../../modals/add-edit-policy.modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { insuranceTypes } from '../../constants/insurace-type.constants';
import { coverageTypes } from '../../constants/coverage-type.constants';
import { Dropdown } from '../dropdown/dropdown.comp';
import { toast } from '../../utils/toast.utils';
import { useNavigate } from 'react-router-dom';

type PoliciesTableProps = {
    carrier: Carrier;
};

export const PoliciesTable = ({
    carrier,
}: PoliciesTableProps) => {
    const [policiesList, setPoliciesList] = useState(carrier.policies);
    const [isLoading, setLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [currentPolicy, setCurrentPolicy] = useState<Policy>();
    const [query, setQuery] = useState('');
    const [stateName, setStateName] = useState<string>();
    const [insuranceType, setInsuranceType] = useState<InsuranceType>();
    const [coverageType, setCoverageType] = useState<CoverageType>();

    const states = carrier.policies.flatMap((p) => p.states ?? []);

    const navigate = useNavigate();

    const onRowClick = (policyId: number) => {
        navigate(`/carriers/${carrier.id}/policies/${policyId}`);
    };

    const handleUpdate = async (id: number, policy: Omit<PolicyUpdateRequest, 'id'>) => {
        try {
            setLoading(true);
            const { policies } = await carriersService.updateCarrierById(carrier.id, {
                policies: {
                    update: [{ id, ...policy }],
                },
            });
            setPoliciesList(policies);
        } catch (error) { } finally {
            setLoading(false);
            onCloseModal();
        }
    };

    const handleAddRow = async (data: PickPolicyCreateRequestExcludeKeyofPolicyCreateRequestCarrierId) => {
        try {
            setLoading(true);
            const { policies: response } = await carriersService.updateCarrierById(carrier.id, {
                policies: {
                    add: [{ ...data }],
                },
            });
            setPoliciesList(response);

        } catch (error) { } finally {
            setLoading(false);
            onCloseModal();
        }
    };

    const handleDeleteRow = async (id: number) => {
        try {
            setPoliciesList(policiesList.filter((_) => _.id !== id));
            await carriersService.updateCarrierById(carrier.id, {
                policies: {
                    remove: [id],
                },
            });
        } catch (error) {
            console.error('Failed to remove policy', error);
        } finally {
            onCloseModal();
        }
    };

    const search = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setQuery(e.target.value);
    };

    const handleSetStateName = (name?: string) => {
        setStateName(states.find((s) => s.name === name)?.name);
        setPoliciesList(carrier.policies);
    };

    const handleInsuranceTypeSelect = (name?: string) => {
        const found = Object.keys(insuranceTypes).find((key) => insuranceTypes[key as keyof typeof insuranceTypes] === name);
        setInsuranceType(found as InsuranceType);
    };

    const handleCoverageTypeSelect = (name?: string) => {
        const found = Object.keys(coverageTypes).find((key) => coverageTypes[key as keyof typeof coverageTypes] === name);
        setCoverageType(found as CoverageType);
    };

    const onCloseModal = () => {
        setCurrentPolicy(undefined);
        onClose();
    };

    useEffect(() => {
        const policies = carrier.policies.filter((p) => {
            const hasQuery = query.trim() ? p.name.includes(query.trim()) : true;
            const hasState = stateName ? p.states?.map((s) => s.name).includes(stateName) : true;
            const hasInsuranceType = insuranceType ? p.insuranceType === insuranceType : true;
            const hasCoverageType = coverageType ? p.coverageType === coverageType : true;
            return hasQuery && hasState && hasInsuranceType && hasCoverageType;
        });
        setPoliciesList(policies);
    }, [stateName, query, insuranceType, coverageType]);

    return (
        <Box mr={3}>
            <Flex mb={2}>
                <Flex>
                    <Flex mr={3}>
                        <Input onChange={search} value={query} placeholder="search..." type={'search'} />
                        <Button type="submit" ml={2} colorScheme="primary">Search</Button>
                    </Flex>
                    <Box mr={2}>
                        <Dropdown
                            items={Array.from(new Set(states.map((s) => s.name)))}
                            onChange={handleSetStateName}
                            placeholder='State'
                            value={stateName}
                            name='state'
                        />
                    </Box>
                    <Box mr={2}>
                        <Dropdown
                            items={Object.values(insuranceTypes)}
                            onChange={handleInsuranceTypeSelect}
                            placeholder='Insurance'
                            value={insuranceTypes[Object.keys(insuranceTypes).find((key) => key === insuranceType) as keyof typeof insuranceTypes]}
                            name='insuranceType'
                        />
                    </Box>
                    <Box mr={2}>
                        <Dropdown
                            items={Object.values(coverageTypes)}
                            onChange={handleCoverageTypeSelect}
                            placeholder='Coverage'
                            value={coverageTypes[Object.keys(coverageTypes).find((key) => key === coverageType) as keyof typeof coverageTypes]}
                            name='coverageType'
                        />
                    </Box>
                </Flex>
                <Button
                    ml={'auto'}
                    aria-label='Add Policy'
                    colorScheme="teal"
                    onClick={onOpen}
                >
                    Add Policy
                </Button>
            </Flex>
            {!isLoading ? (
                <TableContainer>
                    <Table variant="striped">
                        <Thead>
                            <Tr>
                                <Th>Name</Th>
                                <Th>Insurance Type</Th>
                                <Th>Coverage Type</Th>
                                <Th>States</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {policiesList?.length ? (
                                policiesList.map((policy, index) => (
                                    <Tr
                                        key={index}
                                        onClick={() => onRowClick(policy.id)}
                                        _hover={{
                                            cursor: 'pointer',
                                            backgroundColor: 'primary.50',
                                            boxShadow: 'md',
                                            transition: '0.2s ease-in-out',
                                        }}
                                    >
                                        <Td>{policy.name}</Td>
                                        <Td>{insuranceTypes[policy.insuranceType]}</Td>
                                        <Td>{coverageTypes[policy.coverageType]}</Td>
                                        <Td>{policy.states?.map((s) => s.id).join(',') ?? 'Unknown'}</Td>
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan={5} textAlign="center">
                                        No Policies Found.
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            ) : (
                <Spinner size="lg" />
            )}
            <AddEditPolicyModal
                isOpen={isOpen}
                onClose={onCloseModal}
                handleEditPolicy={handleUpdate}
                handleAddPolicy={handleAddRow}
                handleDeletePolicy={handleDeleteRow}
                policy={currentPolicy}
            />
        </Box>
    );
};
